import axios from "axios";
// utils
import { MEDIA_SERVER } from "./environment";
import { normalizeFormDataImages } from "../utils/imageOrientation";
import {
  asciiFormDataFilenames,
  asciiPlaceholderName,
  hasNonAscii
} from "../utils/uploadFilenames";

const mediaServerAddress = axios.create({
  baseURL: `${MEDIA_SERVER}`,
  headers: {
    "Content-Type": "multipart/form-data"
  }
});

const isPost = (req) =>
  !!req && (!req.method || req.method.toLowerCase() === "post");

// Match the image upload endpoint regardless of leading slash or absolute URL.
const isImageUpload = (req) => {
  if (!isPost(req)) return false;
  if (!(req.data instanceof FormData)) return false;
  const url = req.url || "";
  return /(^|\/)image\/?($|\?)/.test(url);
};

// Multipart uploads whose filename the upload service stores as `name`
// (see utils/uploadFilenames.js for why non-ASCII names must not reach it).
const MEDIA_UPLOAD_RE = /(^|\/)(image|images|video|videos)\/?($|\?)/;
const mediaUploadKind = (req) => {
  const m = MEDIA_UPLOAD_RE.exec(req.url || "");
  return m ? m[2] : null;
};

// The chunked video flow sends the name as JSON instead of multipart; the
// service builds the S3 key from it on initiate and the `name` on complete.
const CHUNKED_VIDEO_RE = /(^|\/)(initiate-video-upload|complete-video-upload)\/?($|\?)/;

mediaServerAddress.interceptors.request.use(
  async (req) => {
    const token = localStorage.getItem("token");
    req.headers.authorization = token ? `Bearer ${token}` : null;

    // Bake EXIF orientation into raw pixels for JPEG uploads. Failures inside
    // the helper fall back to the original file, so this can never block an
    // upload — at worst we ship the un-normalized image (today's behavior).
    if (isImageUpload(req)) {
      req.data = await normalizeFormDataImages(req.data);
    }

    if (isPost(req) && req.data instanceof FormData && mediaUploadKind(req)) {
      const { formData, realNames } = asciiFormDataFilenames(req.data);
      req.data = formData;
      if (realNames.some(Boolean)) req.realMediaNames = realNames;
    }

    if (
      isPost(req) &&
      CHUNKED_VIDEO_RE.test(req.url || "") &&
      req.data &&
      typeof req.data === "object" &&
      hasNonAscii(req.data.originalname)
    ) {
      const realName = req.data.originalname;
      req.data = {
        ...req.data,
        originalname: asciiPlaceholderName(realName, "video")
      };
      if (/complete-video-upload/.test(req.url)) req.realMediaNames = [realName];
    }

    return req;
  },
  (error) => Promise.reject(error)
);

// Auth and validation failures will not change on a retry; anything else
// (network drop, 5xx, a server that is still restarting) gets two more tries.
const RENAME_RETRY_DELAYS_MS = [500, 1500];
const isFinalRenameError = (err) => {
  const status = err && err.response && err.response.status;
  return status === 401 || status === 403 || status === 422;
};
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const patchMediaName = async (kind, id, name) => {
  for (let attempt = 0; ; attempt++) {
    try {
      // Global axios: baseURL is our API server and the token interceptor
      // adds the Authorization header.
      await axios.patch(`${kind}/${id}`, { name });
      return;
    } catch (err) {
      if (isFinalRenameError(err) || attempt >= RENAME_RETRY_DELAYS_MS.length) {
        throw err;
      }
      await wait(RENAME_RETRY_DELAYS_MS[attempt]);
    }
  }
};

// After the upload service has created the document(s) under the
// placeholder, write the real name through our own server. The document in
// the response is updated only when the write succeeded, so the UI never
// shows a name the database does not hold.
const restoreRealNames = async (res) => {
  const realNames = res && res.config && res.config.realMediaNames;
  if (!realNames || !res.data) return res;

  const url = res.config.url || "";
  const kind = /video/.test(url) ? "video" : "image";
  const docs = Array.isArray(res.data[`${kind}s`])
    ? res.data[`${kind}s`]
    : res.data[kind]
    ? [res.data[kind]]
    : [];

  await Promise.all(
    docs.map(async (doc, index) => {
      const name = realNames[index];
      if (!name || !doc || !doc._id) return;
      try {
        await patchMediaName(kind, doc._id, name);
        doc.name = name;
      } catch (err) {
        console.warn(
          `[mediaServerAddress] could not restore the ${kind} name "${name}" on ${doc._id}:`,
          err && err.message ? err.message : err
        );
      }
    })
  );
  return res;
};

mediaServerAddress.interceptors.response.use(restoreRealNames, (error) =>
  Promise.reject(error)
);

export default mediaServerAddress;
